import { useEffect, useRef, useCallback } from 'react'

interface UseActiveTimeOptions {
  idleTimeout?: number // Time in ms before user is considered idle (default: 30000)
  heartbeatInterval?: number // Interval for heartbeat updates (default: 10000)
  onActiveTimeUpdate?: (activeTimeMs: number) => void
  onPageLeave?: (totalActiveTimeMs: number) => void
}

interface ActiveTimeState {
  isActive: boolean
  totalActiveTime: number
  lastActiveTimestamp: number
  sessionStartTime: number
}

export function useActiveTime(options: UseActiveTimeOptions = {}) {
  const {
    idleTimeout = 30000, // 30 seconds
    heartbeatInterval = 10000, // 10 seconds
    onActiveTimeUpdate,
    onPageLeave
  } = options

  const stateRef = useRef<ActiveTimeState>({
    isActive: true,
    totalActiveTime: 0,
    lastActiveTimestamp: Date.now(),
    sessionStartTime: Date.now()
  })

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Update active time calculation
  const updateActiveTime = useCallback(() => {
    const state = stateRef.current
    if (state.isActive) {
      const now = Date.now()
      const elapsed = now - state.lastActiveTimestamp
      state.totalActiveTime += elapsed
      state.lastActiveTimestamp = now
    }
  }, [])

  // Mark user as active
  const markActive = useCallback(() => {
    const state = stateRef.current
    const now = Date.now()

    if (!state.isActive) {
      // User was idle, now active again
      state.isActive = true
      state.lastActiveTimestamp = now
    }

    // Reset idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
    }

    idleTimerRef.current = setTimeout(() => {
      // User has been idle for idleTimeout
      updateActiveTime()
      stateRef.current.isActive = false
    }, idleTimeout)
  }, [idleTimeout, updateActiveTime])

  // Mark user as inactive (tab hidden, window blurred)
  const markInactive = useCallback(() => {
    updateActiveTime()
    stateRef.current.isActive = false

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [updateActiveTime])

  // Get current total active time
  const getActiveTime = useCallback(() => {
    updateActiveTime()
    return stateRef.current.totalActiveTime
  }, [updateActiveTime])

  // Reset the timer (for page navigation within SPA)
  const reset = useCallback(() => {
    stateRef.current = {
      isActive: true,
      totalActiveTime: 0,
      lastActiveTimestamp: Date.now(),
      sessionStartTime: Date.now()
    }
  }, [])

  useEffect(() => {
    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ]

    // Throttle activity detection to avoid too many updates
    let lastActivityTime = 0
    const throttleMs = 1000 // Only process activity once per second

    const handleActivity = () => {
      const now = Date.now()
      if (now - lastActivityTime > throttleMs) {
        lastActivityTime = now
        markActive()
      }
    }

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        markInactive()
      } else {
        markActive()
      }
    }

    // Handle window blur/focus
    const handleBlur = () => markInactive()
    const handleFocus = () => markActive()

    // Handle page unload
    const handleBeforeUnload = () => {
      updateActiveTime()
      const totalTime = stateRef.current.totalActiveTime
      onPageLeave?.(totalTime)
    }

    // Set up heartbeat for periodic updates
    heartbeatTimerRef.current = setInterval(() => {
      if (stateRef.current.isActive) {
        updateActiveTime()
        onActiveTimeUpdate?.(stateRef.current.totalActiveTime)
      }
    }, heartbeatInterval)

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Start idle timer
    markActive()

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeunload', handleBeforeUnload)

      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current)
      }

      // Final update
      updateActiveTime()
      onPageLeave?.(stateRef.current.totalActiveTime)
    }
  }, [markActive, markInactive, updateActiveTime, onActiveTimeUpdate, onPageLeave, heartbeatInterval])

  return {
    getActiveTime,
    reset,
    isActive: () => stateRef.current.isActive
  }
}
